import { Box, SxProps } from '@mui/material';
import { colors } from '@sweep-io/sweep-design';
import { useRef } from 'react';
import {
  NestedFieldsField,
  NestedFieldsSelector,
} from '../common/fieldsSelectors/NestedFieldsSelector';
import { Plus } from '@sweep-io/sweep-design/dist/icons';
import { useSweepFieldsInfo } from '../../sweep-fields/useSweepFieldsInfo';
import React from 'react';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { KEY_ENTER_COMMAND, COMMAND_PRIORITY_HIGH } from 'lexical';
import { DynamicTagNode } from './lexical-editor/DynamicTagNode';
import { DynamicTagPlugin, INSERT_DYNAMIC_TAG_COMMAND } from './lexical-editor/DynamicTagPlugin';
import {
  messageStructToLexicalState,
  lexicalStateToMessageStruct,
} from './lexical-editor/convertMessageStruct';
import { telemetry } from '../../telemetry';

interface InputWithVariablesProps {
  readonly?: boolean;
  data: SlackMessageStruct;
  placeholder?: string;
  onChange: (messageStruct: SlackMessageStruct) => any;
  withBorder?: boolean;
  crmOrgId: string;
  objectName: string;
  customOnKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => any;
  isBigInput?: boolean;
  isShowFieldSelectorAlways?: boolean;
  variableButtonSX?: SxProps;
  isPreventLineBreaks?: boolean;
}

const lexicalConfig = {
  namespace: 'InputWithVariables',
  onError: (error: Error) => {
    telemetry.captureError(error, { errorInfo: error.message });
  },
  nodes: [DynamicTagNode],
  theme: {},
};

function EditorContent({
  readonly,
  data,
  placeholder,
  onChange,
  withBorder,
  crmOrgId,
  objectName,
  customOnKeyDown,
  isBigInput,
  isShowFieldSelectorAlways,
  variableButtonSX,
  isPreventLineBreaks,
}: InputWithVariablesProps) {
  const [editor] = useLexicalComposerContext();
  const { getLabelFromIdsWithObjectType } = useSweepFieldsInfo();

  const initializedRef = useRef(false);

  React.useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (!data?.template) return;

    const fieldLabelResolver = (fieldIds: string[]) => {
      const labels = getLabelFromIdsWithObjectType(fieldIds);
      return labels.filter(Boolean).join('.');
    };

    const serializedState = messageStructToLexicalState(data, fieldLabelResolver);

    try {
      const parsedState = editor.parseEditorState(JSON.stringify(serializedState));
      editor.setEditorState(parsedState);
    } catch (e) {
      telemetry.captureError(e, { errorInfo: 'Failed to load initial editor state from data' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    editor.setEditable(!readonly);
  }, [editor, readonly]);

  React.useEffect(() => {
    if (!isPreventLineBreaks) return;
    return editor.registerCommand(KEY_ENTER_COMMAND, () => true, COMMAND_PRIORITY_HIGH);
  }, [editor, isPreventLineBreaks]);

  // Use a ref for onChange so the blur effect doesn't re-run when onChange changes
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    return editor.registerRootListener((rootElement, prevRootElement) => {
      const handleBlur = () => {
        const newStruct = lexicalStateToMessageStruct(editor.getEditorState());
        onChangeRef.current(newStruct);
      };
      if (prevRootElement) {
        prevRootElement.removeEventListener('blur', handleBlur);
      }
      if (rootElement) {
        rootElement.addEventListener('blur', handleBlur);
      }
    });
  }, [editor]);

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        fontWeight: '400',
        flexGrow: 1,
        minWidth: 0,
        position: 'relative',
        '&:hover .variableButton': {
          visibility: 'visible',
        },
      }}
    >
      <Box
        sx={{
          background: '#FFFFFF',
          fontWeight: '400',
          position: 'relative',
          '& .input-with-variables-editor': {
            outline: 'none',
            padding: '9px 15px',
            paddingRight: isShowFieldSelectorAlways ? 0 : '170px',
            paddingBottom: isShowFieldSelectorAlways ? '34px' : 0,
            border: withBorder ? `1px solid ${colors.grey[300]}` : undefined,
            '&:focus-within': {
              border: withBorder ? `1px solid ${colors.blue[500]}` : undefined,
            },
            whiteSpace: 'nowrap',
            height: isBigInput ? '160px' : '40px',
            overflow: isBigInput ? 'auto' : 'hidden',
            fontFamily: 'Inter',
            fontSize: '14px',
            lineHeight: '1.42',
            '& p': {
              margin: 0,
            },
          },
        }}
        onKeyDownCapture={(e) => {
          if (customOnKeyDown) {
            customOnKeyDown(e as unknown as React.KeyboardEvent<HTMLInputElement>);
          }
        }}
      >
        <PlainTextPlugin
          contentEditable={<ContentEditable className="input-with-variables-editor" />}
          placeholder={
            <Box
              sx={{
                position: 'absolute',
                top: '9px',
                left: '15px',
                color: colors.grey[500],
                fontSize: '14px',
                fontFamily: 'Inter',
                fontStyle: 'normal',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            >
              {placeholder ?? 'Value'}
            </Box>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <DynamicTagPlugin />
      </Box>
      <Box
        className={'variableButton'}
        sx={{
          position: 'absolute',
          right: '1px',
          lineHeight: '34px',
          borderRadius: '4px',
          top: '1px',
          visibility: isShowFieldSelectorAlways ? 'visible' : 'hidden',
          height: '36px',
          background: colors.white,
          ...variableButtonSX,
        }}
      >
        <NestedFieldsSelector
          readonly={readonly}
          nestedPath={[]}
          customButtonStartIcon={<Plus size="medium" color={colors.blue[500]} />}
          crmOrgId={crmOrgId}
          objectType={objectName}
          useCustomButton
          customButtonText="Add dynamic variable"
          customButtonSx={{
            height: '16px',
            fontSize: '12px',
            background: 'transparent',
            color: colors.blue[500],
            border: 'none',
            textTransform: 'unset',
            pr: '12px',
            '& span': {
              fontWeight: 500,
              fontSize: '12px',
            },
            '&:hover': {
              background: colors.white,
              color: colors.blue[600],
            },
          }}
          onChange={({ fieldIds, fieldContext, fieldLabels }: NestedFieldsField) => {
            const fieldText = fieldLabels.join('.');

            editor.focus();
            setTimeout(() => {
              editor.dispatchCommand(INSERT_DYNAMIC_TAG_COMMAND, {
                tagType: 'field',
                fieldIds,
                fieldContext: fieldContext ?? undefined,
                label: fieldText,
              });
            }, 200);
          }}
        />
      </Box>
    </Box>
  );
}

export const InputWithVariablesLexical = React.memo((props: InputWithVariablesProps) => {
  return (
    <LexicalComposer initialConfig={lexicalConfig}>
      <EditorContent {...props} />
    </LexicalComposer>
  );
});
